/* RESPONSIBLE TEAM: team-knowledge-foundations */

import { renderBlocksV1 } from 'embercom/helpers/render-blocks';
import type { Block } from '@intercom/interblocks.ts';

let currentOverlay: HTMLElement | null = null;

function findBlockContainer(): Element | null {
  return (
    document.querySelector('.ProseMirror') ??
    document.querySelector('.intercom-interblocks') ??
    document.querySelector('.external__content__view__body__markdown-container')
  );
}

function findBlockAtIndex(container: Element, index: number): HTMLElement | undefined {
  let element = container.children[index] as HTMLElement | undefined;
  if (element?.classList.contains('ProseMirror-gapcursor')) {
    element = container.children[index + 1] as HTMLElement | undefined;
  }
  return element;
}

export function removeBlockHighlight(): void {
  if (currentOverlay) {
    currentOverlay.remove();
    currentOverlay = null;
  }
}

function scrollToElement(scrollingContainer: HTMLElement, targetElement: HTMLElement): void {
  let containerRect = scrollingContainer.getBoundingClientRect();
  let elementRect = targetElement.getBoundingClientRect();
  let containerPadding = 65;
  let visibleAreaCenter = (containerRect.height - containerPadding) / 2 - containerPadding;
  let scrollTop =
    scrollingContainer.scrollTop + elementRect.top - containerRect.top - visibleAreaCenter;
  let maxScrollTop = scrollingContainer.scrollHeight - scrollingContainer.clientHeight;
  let clampedScrollTop = Math.max(0, Math.min(scrollTop, maxScrollTop));

  scrollingContainer.scrollTo({ top: clampedScrollTop, behavior: 'smooth' });
}

function createHighlightOverlay(
  targetElement: HTMLElement,
  positionedParent: HTMLElement,
): HTMLElement {
  let parentRect = positionedParent.getBoundingClientRect();
  let blockRect = targetElement.getBoundingClientRect();

  let overlay = document.createElement('div');
  overlay.className = 'content-readiness-block-highlight';
  overlay.style.top = `${blockRect.top - parentRect.top}px`;
  overlay.style.left = `${blockRect.left - parentRect.left}px`;
  overlay.style.width = `${blockRect.width}px`;
  overlay.style.height = `${blockRect.height}px`;

  return overlay;
}

export function buildDiffDom(blocks: Block[]): HTMLElement {
  let container = document.createElement('div');
  container.className = 'content-readiness-diff-overlay content-readiness-diff-after';

  let content = renderBlocksV1(blocks, false, undefined);
  container.appendChild(content);

  return container;
}

export function findBlockElement(blockIndex: number): HTMLElement | undefined {
  let blockContainer = findBlockContainer();
  if (!blockContainer) {
    return undefined;
  }
  return findBlockAtIndex(blockContainer, blockIndex);
}

export function scrollToBlock(blockIndex: number): void {
  let blockContainer = findBlockContainer();
  if (!blockContainer) {
    return;
  }

  let blockElement = findBlockAtIndex(blockContainer, blockIndex);
  if (!blockElement) {
    return;
  }

  let scrollingContainer = document.getElementById('content-readiness-scrolling-container');
  if (!scrollingContainer) {
    return;
  }

  scrollToElement(scrollingContainer, blockElement);
}

export function scrollToAndHighlightBlock(blockIndex: number): void {
  removeBlockHighlight();

  let blockContainer = findBlockContainer();
  if (!blockContainer) {
    return;
  }

  let blockElement = findBlockAtIndex(blockContainer, blockIndex);
  if (!blockElement) {
    return;
  }

  let scrollingContainer = document.getElementById('content-readiness-scrolling-container');
  if (!scrollingContainer) {
    return;
  }

  let positionedParent = scrollingContainer.firstElementChild as HTMLElement | null;
  if (!positionedParent) {
    return;
  }

  scrollToElement(scrollingContainer, blockElement);

  let highlight = createHighlightOverlay(blockElement, positionedParent);
  positionedParent.appendChild(highlight);
  currentOverlay = highlight;
}

export function scrollToAndHighlightTitle(): void {
  removeBlockHighlight();

  let titleElement = document.querySelector<HTMLElement>('[data-kh-editor-title]');
  if (!titleElement) {
    return;
  }

  let scrollingContainer = document.getElementById('content-readiness-scrolling-container');
  if (!scrollingContainer) {
    return;
  }

  let positionedParent = scrollingContainer.firstElementChild as HTMLElement | null;
  if (!positionedParent) {
    return;
  }

  scrollToElement(scrollingContainer, titleElement);

  let highlight = createHighlightOverlay(titleElement, positionedParent);
  positionedParent.appendChild(highlight);
  currentOverlay = highlight;
}
